// frontend/src/services/manualWaypointService.js
// Service for manual waypoint override operations

import axios from '../axiosConfig';

/**
 * Manual Waypoint Service
 *
 * Provides API calls for manually overriding waypoint state.
 * Used when GPS-based proximity detection fails (e.g., airport terminals with ~1km zones).
 */
const manualWaypointService = {
    /**
     * Mark a waypoint as arrived manually
     *
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number} waypointId - Waypoint ID to mark as arrived
     * @returns {Promise<Object>} Response with arrivalCreated, alreadyArrived flags
     */
    async markArrived(entityId, tripId, waypointId) {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint/${waypointId}/manual-status`,
            { action: 'arrived' }
        );
        return response.data;
    },

    /**
     * Mark a waypoint as departed manually
     *
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number} waypointId - Waypoint ID to mark as departed
     * @returns {Promise<Object>} Response with departureCreated, alreadyDeparted flags
     */
    async markDeparted(entityId, tripId, waypointId) {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint/${waypointId}/manual-status`,
            { action: 'departed' }
        );
        return response.data;
    },

    /**
     * Correct a waypoint (clear existing passage data)
     * Used when a waypoint was incorrectly marked
     *
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number} waypointId - Waypoint ID to correct
     * @param {string} reason - Reason for correction
     * @returns {Promise<Object>} Response with correctionApplied flag
     */
    async correctWaypoint(entityId, tripId, waypointId, reason) {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint/${waypointId}/manual-status`,
            { action: 'correct', reason }
        );
        return response.data;
    },

    /**
     * Skip a waypoint manually.
     *
     * @param {number} entityId
     * @param {number} tripId
     * @param {number} waypointId
     * @returns {Promise<Object>}
     */
    async skipWaypoint(entityId, tripId, waypointId) {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint/${waypointId}/manual-status`,
            { action: 'skip' }
        );
        return response.data;
    },

    /**
     * Get correction options without mutating state.
     *
     * @param {number} entityId
     * @param {number} tripId
     * @param {number} waypointId
     * @returns {Promise<Object>}
     */
    async getCorrectionOptions(entityId, tripId, waypointId) {
        const response = await axios.get(
            `/api/trips/${entityId}/${tripId}/waypoint/${waypointId}/correction-options`
        );
        return response.data;
    },

    /**
     * Apply correction and optional selected co-located waypoint.
     *
     * @param {number} entityId
     * @param {number} tripId
     * @param {number} waypointId
     * @param {number|null} selectedWaypointId
     * @param {string} reason
     * @returns {Promise<Object>}
     */
    async selectAfterCorrect(entityId, tripId, waypointId, selectedWaypointId = null, reason = '') {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint/${waypointId}/select-after-correct`,
            { selectedWaypointId, reason }
        );
        return response.data;
    }
};

export default manualWaypointService;
